/**
 * API handler for generating or retrieving a Third Card.
 *
 * This module encapsulates the logic for looking up an existing
 * Third Card in your Cloudflare D1 database and, if absent,
 * generating a new card using OpenAI's API.  Because this
 * scaffolding is intended to run in a browser (via Vite) for
 * development, actual network calls are not implemented.  Replace
 * placeholder sections with calls to your deployed backend (for
 * example, Cloudflare Pages Functions or Workers) that performs
 * database queries and invokes OpenAI as needed.
 */

// Example of what a Third Card record might look like.  In the real
// implementation, this structure should match your D1 table schema.
// {
//   id: 'uuid',
//   card1_id: 'strength',
//   card2_id: 'tower',
//   card_pair: 'strength-tower',
//   thirdCardTitle: 'Phoenix Rising',
//   description: 'A new dawn emerges from the ashes...',
//   imageURL: 'https://...',
//   created_at: '2025-10-22T00:00:00Z'
// }

export async function generateThirdCard(card1, card2) {
  try {
    console.log('🎯 === THIRD CARD GENERATION STARTED ===');
    console.log('🎯 Card 1:', card1.name, '(' + card1.id + ')', 'Orientation:', card1.orientation || 'upright');
    console.log('🎯 Card 2:', card2.name, '(' + card2.id + ')', 'Orientation:', card2.orientation || 'upright');
    
    // Determine the correct API URL based on environment
    const isLocal = window.location.hostname === 'localhost';
    const apiUrl = isLocal 
      ? '/api/generateThirdCard'  // Use Vite proxy instead of direct port
      : '/api/generateThirdCard';
    
    console.log(`🎯 API URL: ${apiUrl}`);
    console.log(`🎯 Environment: ${isLocal ? 'LOCAL' : 'PRODUCTION'}`);
    
    // Prepare request payload
    const payload = {
      card1,
      card2
    };
    console.log('🎯 Request payload:', JSON.stringify(payload, null, 2));
    
    console.log('🎯 Making fetch request...');
    
    // Call the API to generate or retrieve the third card
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    console.log(`🎯 Response status: ${response.status}`);
    console.log(`🎯 Response ok: ${response.ok}`);
    console.log(`🎯 Response headers:`, Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('🎯 ERROR Response text:', errorText);
      
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        console.error('🎯 Could not parse error as JSON');
      }
      
      throw new Error(errorData.message || `API error: ${response.status} - ${errorText}`);
    }
    
    const responseText = await response.text();
    console.log('🎯 Raw response text:', responseText);
    
    const thirdCard = JSON.parse(responseText);
    console.log('🎯 Parsed third card:', thirdCard);
    console.log('🎯 Third card title:', thirdCard.thirdCardTitle);
    console.log('🎯 === THIRD CARD GENERATION SUCCESS ===');
    
    return thirdCard;
    
  } catch (error) {
    console.error('🎯 === THIRD CARD GENERATION ERROR ===');
    console.error('🎯 Error type:', error.constructor.name);
    console.error('🎯 Error message:', error.message);
    console.error('🎯 Full error:', error);
    console.error('🎯 Falling back to placeholder...');
    
    // Fallback to a mystical placeholder if API fails
    const sortedIds = [card1.id, card2.id].sort();
    const cardPair = sortedIds.join('-');
    
    const fallbackCard = {
      id: crypto.randomUUID(),
      card1_id: card1.id,
      card2_id: card2.id,
      card_pair: cardPair,
      thirdCardTitle: `The Synthesis of ${card1.name} & ${card2.name}`,
      description: `The Oracle whispers of a profound connection between ${card1.name} and ${card2.name}. 
      
In the sacred space where these two energies meet, a new truth emerges. The ancient patterns speak of transformation, of hidden wisdom waiting to be unveiled.

✦ The mystical synthesis awaits deeper contemplation. Please try again as the cosmic energies align.`,
      imageURL: '',
      created_at: new Date().toISOString()
    };
    
    console.log('🎯 Returning fallback card:', fallbackCard);
    return fallbackCard;
  }
}